import {
  ArrowClockwise,
  Info,
  MagnifyingGlass,
  Plus,
  Prohibit,
  WarningCircle,
  X,
} from '@phosphor-icons/react';
import { useQueryClient } from '@tanstack/react-query';
import { format, parseISO } from 'date-fns';
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ConfirmationModal } from '@/components/core';
import { Button } from '@/components/ui-kit/button';
import { Input } from '@/components/ui-kit/input';
import { cn } from '@/lib/utils';
import { useToast } from '@/hooks/use-toast';
import { AddSuppressionDialog } from '../components/add-suppression-dialog/add-suppression-dialog';
import { ContactsPagination } from '../components/contacts-pagination/contacts-pagination';
import {
  useDeleteSuppression,
  useSuppressionsList,
} from '../hooks/use-suppressions';
import { getSuppressionErrorMessage } from '../services/suppressions.service';
import type {
  SuppressionChannelApi,
  SuppressionEntryApi,
  SuppressionReasonApi,
} from '../types/suppressions-api.types';
import {
  getSuppressionConflictMessage,
  getSuppressionHttpStatus,
  isSuppressionNotFoundError,
} from '../utils/suppression-api-errors';

const tableHeadClassName =
  'sticky top-0 z-10 whitespace-nowrap bg-neutral-50 px-4 py-3 text-left text-[13px] font-semibold leading-[18px] text-neutral-600';

const skeletonBlockClassName = 'block animate-pulse rounded bg-[#EDEEF0]';

const REASON_FILTERS: { value: '' | SuppressionReasonApi; label: string }[] = [
  { value: '', label: 'All' },
  { value: 'bounce', label: 'Bounce' },
  { value: 'complaint', label: 'Complaint' },
  { value: 'unsubscribe', label: 'Unsubscribe' },
  { value: 'manual', label: 'Manual' },
];

const SUPPRESSION_REASON_LABEL: Record<SuppressionReasonApi, string> = {
  bounce: 'Bounce',
  complaint: 'Complaint',
  unsubscribe: 'Unsubscribe',
  manual: 'Manual',
};

const CHANNEL_LABEL: Record<SuppressionChannelApi, string> = {
  email: 'Email',
  sms: 'SMS',
};

const newIdempotencyKey = () =>
  typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : `suppression-del-${Date.now()}-${Math.random().toString(36).slice(2)}`;

const getSuppressionReasonPillClassName = (reason: SuppressionReasonApi): string => {
  switch (reason) {
    case 'manual':
      return 'bg-emerald-50 text-emerald-800';
    case 'bounce':
      return 'bg-amber-50 text-amber-900';
    case 'complaint':
      return 'bg-rose-50 text-rose-900';
    case 'unsubscribe':
    default:
      return 'bg-neutral-100 text-neutral-600';
  }
};

const formatEntryDate = (value?: string): string => {
  if (!value) return '—';
  try {
    return format(parseISO(value), 'MMM d, yyyy');
  } catch {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '—';
    return format(date, 'MMM d, yyyy');
  }
};

const getBlockedRemoveDescription = (entry: SuppressionEntryApi): string => {
  if (entry.reason === 'unsubscribe') {
    return 'This entry was created by an unsubscribe, not added manually, so it can only be cleared by a new signal from that source.';
  }
  if (entry.reason === 'bounce' || entry.reason === 'complaint') {
    return 'This entry was created by the sending provider (bounce/complaint), not added manually, so it can only be cleared by a new signal from that source.';
  }
  return 'This entry cannot be removed manually.';
};

const SuppressionSkeletonRows = ({ rowCount }: { rowCount: number }) => (
  <>
    {Array.from({ length: rowCount }, (_, index) => (
      <tr key={`skeleton-${index}`} className="border-b border-neutral-100">
        <td className="px-4 py-3">
          <span className={cn(skeletonBlockClassName, 'h-3.5 w-[180px]')} aria-hidden />
        </td>
        <td className="px-4 py-3">
          <span className={cn(skeletonBlockClassName, 'h-3.5 w-12')} aria-hidden />
        </td>
        <td className="px-4 py-3">
          <span className={cn(skeletonBlockClassName, 'h-5 w-[88px] rounded-full')} aria-hidden />
        </td>
        <td className="px-4 py-3">
          <span className={cn(skeletonBlockClassName, 'h-3.5 w-[72px]')} aria-hidden />
        </td>
        <td className="px-4 py-3">
          <span className={cn(skeletonBlockClassName, 'h-3.5 w-[96px]')} aria-hidden />
        </td>
        <td className="px-4 py-3">
          <span className={cn(skeletonBlockClassName, 'h-3.5 w-[70px]')} aria-hidden />
        </td>
        <td className="px-4 py-3" />
      </tr>
    ))}
  </>
);

export const SuppressionPage = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { mutateAsync: deleteSuppression, isPending: isDeleting } = useDeleteSuppression();
  const deleteIdempotencyKeyRef = useRef<string | null>(null);
  const deleteInFlightRef = useRef(false);

  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [reasonFilter, setReasonFilter] = useState<'' | SuppressionReasonApi>('');
  const [pageNo, setPageNo] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [entryToRemove, setEntryToRemove] = useState<SuppressionEntryApi | null>(null);
  const [blockedEntry, setBlockedEntry] = useState<SuppressionEntryApi | null>(null);
  const [blockedMessage, setBlockedMessage] = useState<string | null>(null);

  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedSearch(search.trim()), 350);
    return () => window.clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    setPageNo(1);
  }, [debouncedSearch, reasonFilter]);

  useEffect(() => {
    setEntryToRemove(null);
    setBlockedEntry(null);
    setBlockedMessage(null);
  }, [search, reasonFilter]);

  const listQuery = useSuppressionsList({
    pageNo,
    pageSize,
    q: debouncedSearch,
    reason: reasonFilter || undefined,
  });

  const entries = listQuery.data?.items ?? [];
  const pageInfo = listQuery.data?.pageInfo;
  const totalCount = pageInfo?.total ?? 0;
  const totalUnfiltered = pageInfo?.totalUnfiltered;
  const hasNextPage = Boolean(pageInfo?.hasNext);
  const hasPreviousPage = Boolean(pageInfo?.hasPrev);
  const totalPages =
    pageSize > 0 && totalCount > 0 ? Math.max(1, Math.ceil(totalCount / pageSize)) : 0;

  const { isLoading, isFetching, isError, refetch } = listQuery;

  useEffect(() => {
    if (totalPages > 0 && pageNo > totalPages) {
      setPageNo(totalPages);
    }
  }, [pageNo, totalPages]);

  const handlePageSizeChange = (nextPageSize: number) => {
    queryClient.removeQueries({ queryKey: ['suppressions'] });
    setPageSize(nextPageSize);
    setPageNo(1);
  };

  const showLoading = (isLoading || isFetching) && entries.length === 0;
  const showErrorState = !showLoading && isError;
  const showEmptyState = !showLoading && !isError && entries.length === 0;
  const skeletonRowCount = Math.max(8, Math.min(pageSize, 12));
  const hasActiveFilters = Boolean(reasonFilter || debouncedSearch);

  const subtitle = (() => {
    if (showLoading) return 'Loading…';
    if (isError) return 'Could not load suppressions.';
    if (totalCount === 0) return '0 suppressed addresses';

    const reasonSuffix = reasonFilter
      ? ` · filtered to ${SUPPRESSION_REASON_LABEL[reasonFilter]}`
      : '';

    if (
      hasActiveFilters &&
      typeof totalUnfiltered === 'number' &&
      totalUnfiltered !== totalCount
    ) {
      return `${totalCount.toLocaleString()} of ${totalUnfiltered.toLocaleString()} suppressed addresses${reasonSuffix}`;
    }

    return `${totalCount.toLocaleString()} suppressed address${
      totalCount === 1 ? '' : 'es'
    }${reasonSuffix}`;
  })();

  const openBlockedDialog = (entry: SuppressionEntryApi, message?: string) => {
    setEntryToRemove(null);
    setBlockedEntry(entry);
    setBlockedMessage(message ?? getBlockedRemoveDescription(entry));
  };

  const handleRemoveClick = (entry: SuppressionEntryApi) => {
    if (entry.reason === 'manual') {
      setBlockedEntry(null);
      setBlockedMessage(null);
      deleteIdempotencyKeyRef.current = newIdempotencyKey();
      setEntryToRemove(entry);
      return;
    }
    openBlockedDialog(entry);
  };

  const handleRemoveConfirm = async () => {
    if (!entryToRemove || deleteInFlightRef.current || isDeleting) return;

    deleteInFlightRef.current = true;
    try {
      await deleteSuppression({
        itemId: entryToRemove.itemId,
        idempotencyKey: deleteIdempotencyKeyRef.current ?? newIdempotencyKey(),
      });
      toast({
        variant: 'success',
        title: 'Removed from suppression',
        description: `${entryToRemove.address} can receive ${CHANNEL_LABEL[entryToRemove.channel].toLowerCase()} sends again.`,
      });
      setEntryToRemove(null);
      deleteIdempotencyKeyRef.current = null;
    } catch (error) {
      if (isSuppressionNotFoundError(error)) {
        setEntryToRemove(null);
        deleteIdempotencyKeyRef.current = null;
        toast({
          variant: 'destructive',
          title: 'This entry is no longer available.',
        });
        void refetch();
        return;
      }

      if (getSuppressionHttpStatus(error) === 409) {
        const conflict =
          getSuppressionConflictMessage(error) ?? getBlockedRemoveDescription(entryToRemove);
        openBlockedDialog(entryToRemove, conflict);
        deleteIdempotencyKeyRef.current = null;
        return;
      }

      toast({
        variant: 'destructive',
        title: 'Failed to remove',
        description: getSuppressionErrorMessage(error),
      });
    } finally {
      deleteInFlightRef.current = false;
    }
  };

  const handleCreated = (entry: SuppressionEntryApi) => {
    toast({
      variant: 'success',
      title: 'Added to suppression',
      description: `${entry.address} will no longer receive email sends.`,
    });
    if (pageNo !== 1) {
      setPageNo(1);
    } else {
      void refetch();
    }
  };

  const tableHeader = (
    <thead>
      <tr className="border-b border-neutral-200 bg-neutral-50">
        <th className={tableHeadClassName}>Address</th>
        <th className={tableHeadClassName}>Channel</th>
        <th className={tableHeadClassName}>Reason</th>
        <th className={tableHeadClassName}>Source</th>
        <th className={tableHeadClassName}>Contact</th>
        <th className={tableHeadClassName}>Date</th>
        <th className={cn(tableHeadClassName, 'w-14')} />
      </tr>
    </thead>
  );

  return (
    <div className="flex h-full min-h-0 min-w-0 w-full max-w-full flex-col gap-5 overflow-x-hidden">
      <div className="flex items-start gap-2.5 rounded-lg bg-primary-50 px-3.5 py-3">
        <Info className="mt-0.5 size-4 shrink-0 text-primary-600" aria-hidden />
        <p className="text-[13px] leading-[18px] text-primary-900">
          This list is the per-channel do-not-send gate. Bounce and complaint entries come from the
          sending provider; unsubscribe entries from opt-out. Only manual entries can be added or
          removed here.
        </p>
      </div>

      <div className="flex flex-wrap items-start gap-4">
        <div>
          <h1 className="text-[28px] font-semibold leading-9 text-neutral-900">Suppression</h1>
          <p className="mt-0.5 text-sm leading-5 text-neutral-600">{subtitle}</p>
        </div>
        <div className="ml-auto">
          <Button
            type="button"
            className="h-10 gap-2 bg-primary-600 px-4 text-white shadow-e1 hover:bg-primary-700"
            onClick={() => setIsAddOpen(true)}
          >
            <Plus className="size-4" aria-hidden />
            Add to suppression
          </Button>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative min-w-[220px] flex-1">
          <MagnifyingGlass
            className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-neutral-500"
            aria-hidden
          />
          <Input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search by address"
            className="h-10 rounded-md border-neutral-300 pl-9 text-sm"
            aria-label="Search suppressed addresses"
          />
        </div>
        <div className="flex flex-wrap items-center gap-1.5">
          {REASON_FILTERS.map((filter) => {
            const isActive = reasonFilter === filter.value;
            return (
              <button
                key={filter.label}
                type="button"
                className={cn(
                  'rounded-md border px-3 py-[7px] text-[13px] font-medium',
                  isActive
                    ? 'border-primary-600 bg-primary-600 text-white'
                    : 'border-neutral-300 bg-white text-neutral-800 hover:bg-neutral-100'
                )}
                onClick={() => setReasonFilter(filter.value)}
              >
                {filter.label}
              </button>
            );
          })}
        </div>
      </div>

      <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-lg border border-neutral-200 bg-white shadow-e1">
        <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
          {showEmptyState || showErrorState ? (
            <>
              <table className="w-full min-w-[820px] border-collapse text-sm leading-5">
                {tableHeader}
              </table>
              <div className="flex min-h-0 flex-1 items-center justify-center px-4 py-10">
                {showErrorState ? (
                  <div className="mx-auto flex max-w-[360px] flex-col items-center gap-2 text-center">
                    <WarningCircle className="size-7 text-error" aria-hidden />
                    <p className="font-medium text-neutral-900">Could not load suppressions</p>
                    <p className="text-[13px] leading-[18px] text-neutral-500">
                      Something went wrong on our end. Try again.
                    </p>
                    <Button
                      type="button"
                      variant="outline"
                      className="mt-1 h-8 border-neutral-200"
                      onClick={() => void refetch()}
                    >
                      <ArrowClockwise className="size-4" aria-hidden />
                      Retry
                    </Button>
                  </div>
                ) : (
                  <div className="mx-auto flex max-w-[360px] flex-col items-center gap-2 text-center">
                    <Prohibit className="size-7 text-neutral-400" aria-hidden />
                    <p className="font-medium text-neutral-900">
                      No suppressed addresses
                      {hasActiveFilters ? ' match this filter' : ''}
                    </p>
                    <p className="text-[13px] leading-[18px] text-neutral-500">
                      Addresses land here from provider bounces/complaints, unsubscribes, or manual
                      entry.
                    </p>
                  </div>
                )}
              </div>
            </>
          ) : (
            <div className="min-h-0 flex-1 overflow-auto">
              <table className="w-full min-w-[820px] border-collapse text-sm leading-5">
                {tableHeader}
                <tbody>
                  {showLoading ? <SuppressionSkeletonRows rowCount={skeletonRowCount} /> : null}
                  {!showLoading &&
                    entries.map((entry) => {
                      const contactId = entry.contactId?.trim();
                      const removable = entry.reason === 'manual';
                      return (
                        <tr
                          key={entry.itemId}
                          className="border-b border-neutral-100 last:border-b-0 hover:bg-neutral-50"
                        >
                          <td className="px-4 py-3 align-middle font-medium text-neutral-900">
                            {entry.address}
                          </td>
                          <td className="px-4 py-3 align-middle text-neutral-600">
                            {CHANNEL_LABEL[entry.channel] ?? entry.channel}
                          </td>
                          <td className="px-4 py-3 align-middle">
                            <span
                              className={cn(
                                'inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium',
                                getSuppressionReasonPillClassName(entry.reason)
                              )}
                            >
                              {SUPPRESSION_REASON_LABEL[entry.reason] ?? entry.reason}
                            </span>
                          </td>
                          <td className="px-4 py-3 align-middle capitalize text-neutral-600">
                            {entry.source}
                          </td>
                          <td className="px-4 py-3 align-middle">
                            {contactId ? (
                              <Link
                                to={`/contacts/${contactId}`}
                                className="font-medium text-primary-600 hover:text-primary-700"
                              >
                                View
                              </Link>
                            ) : (
                              <span className="text-neutral-400">—</span>
                            )}
                          </td>
                          <td className="whitespace-nowrap px-4 py-3 align-middle text-neutral-600">
                            {formatEntryDate(entry.createdAt)}
                          </td>
                          <td className="px-4 py-3 align-middle text-right">
                            <button
                              type="button"
                              className="inline-flex size-8 items-center justify-center rounded-md text-neutral-500 hover:bg-error-background hover:text-error"
                              title={
                                removable
                                  ? 'Remove'
                                  : 'Not manually removable — provider/unsubscribe entry'
                              }
                              aria-label={
                                removable
                                  ? `Remove ${entry.address} from suppression`
                                  : `Cannot remove ${entry.address}`
                              }
                              onClick={() => handleRemoveClick(entry)}
                            >
                              <X className="size-4" aria-hidden />
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                </tbody>
              </table>
            </div>
          )}
        </div>
        <ContactsPagination
          variant="simple"
          pageNo={pageNo}
          pageSize={pageSize}
          totalCount={showLoading || isError ? 0 : totalCount}
          totalPages={showLoading || isError ? 0 : totalPages}
          totalUnfiltered={
            showLoading || isError || !hasActiveFilters ? undefined : totalUnfiltered
          }
          hasNextPage={!showLoading && !isError && hasNextPage}
          hasPreviousPage={!showLoading && !isError && hasPreviousPage}
          onPageChange={setPageNo}
          onPageSizeChange={handlePageSizeChange}
          itemLabel="addresses"
          isLoading={showLoading}
          isError={isError}
        />
      </div>

      <AddSuppressionDialog
        open={isAddOpen}
        onOpenChange={setIsAddOpen}
        onCreated={handleCreated}
      />

      <ConfirmationModal
        open={Boolean(entryToRemove)}
        onOpenChange={(open) => {
          if (!open && !isDeleting) {
            setEntryToRemove(null);
            deleteIdempotencyKeyRef.current = null;
          }
        }}
        title={`Remove ${entryToRemove?.address ?? ''} from suppression?`}
        description="They will become eligible to receive sends on this channel again."
        confirmText={isDeleting ? 'Removing…' : 'Remove'}
        cancelText="Cancel"
        confirmClassName="bg-destructive text-white"
        confirmDisabled={isDeleting}
        preventAutoClose
        onConfirm={() => {
          void handleRemoveConfirm();
        }}
      />

      <ConfirmationModal
        open={Boolean(blockedEntry)}
        onOpenChange={(open) => {
          if (!open) {
            setBlockedEntry(null);
            setBlockedMessage(null);
          }
        }}
        title="This entry can't be removed manually"
        description={blockedMessage ?? ''}
        confirmText="OK"
        hideCancel
        onConfirm={() => {
          setBlockedEntry(null);
          setBlockedMessage(null);
        }}
      />
    </div>
  );
};
